import { Chip } from '@jotpsych/ui';
import { Button, Stack, Typography } from '@mui/material';

import type { IntakeServiceId } from '../types';

export interface IntakeHubServiceEnrollmentOption {
  id: IntakeServiceId;
  label: string;
  description: string;
}

export interface IntakeHubServiceEnrollmentControlsProps {
  /** 'start' when the scope has no enrollments yet; 'assign' to add another service. */
  mode: 'start' | 'assign';
  /** Human-readable label for the current provider/organization scope. */
  scopeLabel: string;
  /** Services eligible for the current mode (all services for 'start', unenrolled services for 'assign'). */
  serviceOptions: IntakeHubServiceEnrollmentOption[];
  selectedServiceIds: IntakeServiceId[];
  onToggleService: (serviceId: IntakeServiceId) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  /** Disables the submit action independent of selection (e.g. while the dashboard is refetching). */
  isDisabled?: boolean;
}

export function IntakeHubServiceEnrollmentControls({
  mode,
  scopeLabel,
  serviceOptions,
  selectedServiceIds,
  onToggleService,
  onSubmit,
  isSubmitting,
  isDisabled = false,
}: IntakeHubServiceEnrollmentControlsProps) {
  const actionLabel = mode === 'start' ? 'Start intake' : 'Assign service';
  const helperText =
    mode === 'start' ? `Start intake for ${scopeLabel}` : `Assign another service for ${scopeLabel}`;

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      sx={{ alignItems: { xs: 'stretch', sm: 'center' }, flexWrap: 'wrap', rowGap: 1 }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        {helperText}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        {serviceOptions.map(service => (
          <Chip
            key={service.id}
            clickable
            size="small"
            color={selectedServiceIds.includes(service.id) ? 'primary' : 'default'}
            variant={selectedServiceIds.includes(service.id) ? 'filled' : 'outlined'}
            label={service.label}
            onClick={() => onToggleService(service.id)}
          />
        ))}
      </Stack>
      <Button
        size="small"
        variant="contained"
        disabled={isDisabled || isSubmitting || selectedServiceIds.length === 0}
        onClick={onSubmit}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {actionLabel}
      </Button>
    </Stack>
  );
}
